<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const originLat = ref('');
const originLon = ref('');
const travelMode = ref('walking');
const maxTravelTime = ref(30);
const query = ref('');
const maxResults = ref(20);

const isSearching = ref(false);
const results = ref(null);
const error = ref(null);

const travelModes = [
  { value: 'walking', label: t('LIBRARIES.NEARBY.TRAVEL_MODES.WALKING') },
  { value: 'cycling', label: t('LIBRARIES.NEARBY.TRAVEL_MODES.CYCLING') },
  { value: 'driving', label: t('LIBRARIES.NEARBY.TRAVEL_MODES.DRIVING') },
  {
    value: 'driving-traffic',
    label: t('LIBRARIES.NEARBY.TRAVEL_MODES.DRIVING_TRAFFIC'),
  },
  {
    value: 'public_transport',
    label: t('LIBRARIES.NEARBY.TRAVEL_MODES.PUBLIC_TRANSPORT'),
  },
];

const handleSearch = async () => {
  if (!originLat.value || !originLon.value) return;

  isSearching.value = true;
  error.value = null;
  results.value = null;

  try {
    results.value = await store.dispatch('knowledgebase/nearbySearchAction', {
      id: props.knowledgebaseId,
      origin: {
        lat: parseFloat(originLat.value),
        lon: parseFloat(originLon.value),
      },
      travel_mode: travelMode.value,
      max_travel_time_minutes: Number(maxTravelTime.value),
      query: query.value,
      max_results: Number(maxResults.value),
    });
  } catch {
    error.value = t('LIBRARIES.NEARBY.ERROR');
  } finally {
    isSearching.value = false;
  }
};
</script>

<template>
  <div class="flex-1 h-full flex flex-col pt-6 overflow-y-auto">
    <div
      class="bg-slate-25 dark:bg-slate-900 pt-4 pb-0 px-8 border-b border-solid border-slate-50 dark:border-slate-800/50 flex items-center gap-3"
    >
      <h2 class="text-2xl text-slate-800 dark:text-slate-100 mb-1 font-medium">
        {{ t('LIBRARIES.NEARBY.TITLE') }}
      </h2>
      <span
        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 mb-1"
      >
        {{ t('LIBRARIES.NEARBY.BETA') }}
      </span>
    </div>

    <div class="p-8 max-w-2xl">
      <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {{ t('LIBRARIES.NEARBY.DESCRIPTION') }}
      </p>

      <form class="space-y-4" @submit.prevent="handleSearch">
        <!-- Origin -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.NEARBY.ORIGIN_LAT') }}
            </label>
            <input
              v-model="originLat"
              type="number"
              step="any"
              required
              :placeholder="t('LIBRARIES.NEARBY.ORIGIN_LAT_PLACEHOLDER')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.NEARBY.ORIGIN_LON') }}
            </label>
            <input
              v-model="originLon"
              type="number"
              step="any"
              required
              :placeholder="t('LIBRARIES.NEARBY.ORIGIN_LON_PLACEHOLDER')"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- Travel Mode -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('LIBRARIES.NEARBY.TRAVEL_MODE') }}
          </label>
          <select
            v-model="travelMode"
            class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option
              v-for="mode in travelModes"
              :key="mode.value"
              :value="mode.value"
            >
              {{ mode.label }}
            </option>
          </select>
        </div>

        <!-- Max Travel Time + Max Results -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.NEARBY.MAX_TRAVEL_TIME') }}
            </label>
            <input
              v-model="maxTravelTime"
              type="number"
              min="1"
              max="60"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ t('LIBRARIES.NEARBY.MAX_RESULTS') }}
            </label>
            <input
              v-model="maxResults"
              type="number"
              min="1"
              max="100"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <!-- Query -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ t('LIBRARIES.NEARBY.QUERY') }}
          </label>
          <input
            v-model="query"
            type="text"
            :placeholder="t('LIBRARIES.NEARBY.QUERY_PLACEHOLDER')"
            class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <Button
          type="submit"
          :label="t('LIBRARIES.NEARBY.SEARCH_BUTTON')"
          icon="i-lucide-map-pin"
          :disabled="!originLat || !originLon || isSearching"
          :is-loading="isSearching"
        />
      </form>

      <!-- Error -->
      <div
        v-if="error"
        class="mt-6 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm"
      >
        {{ error }}
      </div>

      <!-- Results -->
      <div v-if="results" class="mt-6">
        <!-- Query Info -->
        <div
          class="mb-4 p-3 rounded-md bg-slate-50 dark:bg-slate-800 text-xs text-slate-500 dark:text-slate-400 space-y-1"
        >
          <div>
            {{
              t('LIBRARIES.NEARBY.RESULTS_COUNT', {
                count: results.total_found ?? results.documents?.length ?? 0,
              })
            }}
          </div>
          <div v-if="results.query_info?.radius_m">
            {{
              t('LIBRARIES.NEARBY.RADIUS_M', {
                radius: Math.round(results.query_info.radius_m),
              })
            }}
          </div>
          <div v-if="results.query_info?.isochrone_used !== undefined">
            {{
              results.query_info.isochrone_used
                ? t('LIBRARIES.NEARBY.ISOCHRONE_USED')
                : t('LIBRARIES.NEARBY.RADIUS_APPROX')
            }}
          </div>
          <div v-if="results.query_info?.candidates_before_filter">
            {{
              t('LIBRARIES.NEARBY.CANDIDATES', {
                count: results.query_info.candidates_before_filter,
              })
            }}
          </div>
        </div>

        <!-- Document list -->
        <div v-if="results.documents?.length" class="space-y-3">
          <div
            v-for="(doc, idx) in results.documents"
            :key="doc.id || idx"
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
          >
            <div class="flex items-start justify-between gap-2">
              <h3
                class="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                {{
                  doc.metadata?.title || doc.page_content?.slice(0, 60) || '—'
                }}
              </h3>
              <span
                v-if="doc.metadata?.distance_m"
                class="flex-shrink-0 text-xs text-slate-500 dark:text-slate-400"
              >
                {{
                  t('LIBRARIES.NEARBY.DISTANCE', {
                    distance: Math.round(doc.metadata.distance_m),
                  })
                }}
              </span>
            </div>
            <p
              v-if="doc.page_content"
              class="mt-1 text-xs text-slate-600 dark:text-slate-400 line-clamp-2"
            >
              {{ doc.page_content }}
            </p>
          </div>
        </div>

        <p v-else class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('LIBRARIES.NEARBY.NO_RESULTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
